import { useState } from 'react';
import { useData } from '../context/DataContext';
import { createCategory, updateCategory, deleteCategory } from '../api/api';
import AdminLayout from './AdminLayout';
import './Admin.css';

function CategoryModal({ category, onClose, onSave }) {
  const [formData, setFormData] = useState(category || {
    name: '',
    description: '',
    img: '',
    accent: '#c2a87a'
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      alert('Failed to save category: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{category ? 'Edit Category' : 'Add New Category'}</h2>
          <button className="btn-close" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label>Description *</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="3"
              required
            />
          </div>
          <div className="form-group">
            <label>Image URL *</label>
            <input
              type="url"
              name="img"
              value={formData.img}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label>Accent Color</label>
            <input
              type="color"
              name="accent"
              value={formData.accent}
              onChange={handleChange}
            />
          </div>
          <div className="modal-actions">
            <button type="button" className="btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-save" disabled={loading}>
              {loading ? 'Saving...' : 'Save Category'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AdminCategories({ onLogout }) {
  const { categories, refreshData } = useData();
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const token = localStorage.getItem('adminToken');

  const handleAddCategory = () => {
    setEditingCategory(null);
    setShowModal(true);
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setShowModal(true);
  };

  const handleSaveCategory = async (categoryData) => {
    if (editingCategory) {
      await updateCategory(editingCategory.id, categoryData, token);
    } else {
      await createCategory(categoryData, token);
    }
    await refreshData();
  };

  const handleDeleteCategory = async (id) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      await deleteCategory(id, token);
      await refreshData();
    }
  };

  return (
    <AdminLayout onLogout={onLogout}>
      <div className="admin-header">
        <h1>Categories</h1>
        <p>Manage your flooring categories</p>
      </div>
      <div className="admin-actions">
        <button className="btn-add" onClick={handleAddCategory}>
          + Add New Category
        </button>
      </div>
      <div className="data-table">
        {/* Desktop Table View */}
        <table>
          <thead>
            <tr>
              <th>Image</th>
              <th>Name</th>
              <th>Description</th>
              <th>Accent</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {categories.map(category => (
              <tr key={category.id}>
                <td>
                  <img src={category.img} alt={category.name} />
                </td>
                <td>{category.name}</td>
                <td>{category.description}</td>
                <td>
                  <div
                    style={{
                      width: '30px',
                      height: '30px',
                      backgroundColor: category.accent,
                      borderRadius: '4px',
                      border: '1px solid #ddd'
                    }}
                  />
                </td>
                <td>
                  <div className="table-actions">
                    <button className="btn-edit" onClick={() => handleEditCategory(category)}>
                      Edit
                    </button>
                    <button className="btn-delete" onClick={() => handleDeleteCategory(category.id)}>
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="data-list">
        {categories.map(category => (
          <div key={category.id} className="data-list-item">
            <div className="data-list-content">
              {/* Image on Left */}
              <div className="data-list-image">
                <img src={category.img} alt={category.name} />
              </div>

              {/* Details on Right */}
              <div className="data-list-details">
                <h3 className="data-list-title">{category.name}</h3>
                <p className="data-list-description">{category.description}</p>
                <div className="data-list-accent">
                  <span className="data-list-label">Accent Color:</span>
                  <div
                    className="accent-color-preview"
                    style={{
                      backgroundColor: category.accent,
                    }}
                  />
                  <span className="accent-color-code">{category.accent}</span>
                </div>
              </div>
            </div>

            {/* Buttons Below */}
            <div className="data-list-actions">
              <button className="btn-edit" onClick={() => handleEditCategory(category)}>
                Edit Category
              </button>
              <button className="btn-delete" onClick={() => handleDeleteCategory(category.id)}>
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
      {showModal && (
        <CategoryModal
          category={editingCategory}
          onClose={() => setShowModal(false)}
          onSave={handleSaveCategory}
        />
      )}
    </AdminLayout>
  );
}
